import React, { useContext, useMemo } from 'react';
import ContentLoader from 'react-content-loader';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';

import { Charge } from '@r6digital/rapidstor-types/react/entities/Charge';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import { LineItem } from '@r6digital/rapidstor-types/types/Merchandise';
import { CostRetrieveResponse } from '@r6digital/rapidstor-types/types/Reservation';

import {
    numberRound,
    transformCostRetrieve,
} from '@/features/rapidstor/helpers';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';

import verticalClasses from './PricingTableVertical.module.scss';

interface Props {
    costRetrieve: CostRetrieveResponse | undefined;
    charges: Charge[] | undefined;
    cart?: LineItem[];
    merchandiseDiscount: number;
    showTaxRow?: boolean;
}

export default function VerticalPricingTable(props: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();
    const { currencySymbol } = location.rapidstor;
    const { rapidstorMainColor } = useContext(JourneyContext);
    const {
        costRetrieve, charges, cart, merchandiseDiscount, showTaxRow,
    } = props;

    const totalCost = useMemo(() => {
        if (!costRetrieve && !charges && !cart) return 0;
        return transformCostRetrieve(
            charges,
            costRetrieve,
            cart,
            merchandiseDiscount,
        ).reduce((acc, item) => acc + item.chargeAmount + item.taxAmount, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [costRetrieve, charges, cart, merchandiseDiscount]);

    const totalTax = useMemo(() => {
        if (!costRetrieve && !charges && !cart) return 0;
        return transformCostRetrieve(
            charges,
            costRetrieve,
            cart,
            merchandiseDiscount,
        ).reduce((acc, item) => acc + item.taxAmount, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [costRetrieve, charges, cart, merchandiseDiscount]);

    return (
        <div data-testid="pricing-table" id={verticalClasses.pricingTable}>
            <div className={verticalClasses.pricingTableHeader}>
                <div className={verticalClasses.pricingTableHeaderTitle}>
                    {t('wizard:pricingTable:charge')}
                </div>
                <div
                    className={`${verticalClasses.pricingTableHeaderTitle} ${verticalClasses.fixedWidth}`}
                >
                    {t('wizard:pricingTable:total')}
                </div>
            </div>
            {costRetrieve || charges ? (
                <div className={verticalClasses.pricingTableBody}>
                    {transformCostRetrieve(
                        charges,
                        costRetrieve,
                        cart,
                        merchandiseDiscount,
                    )
                        .filter((item) => item.chargeAmount > 0 || item.discountAmount > 0)
                        .map((item, index) => (
                            <div
                                key={`${index}_crItem_idx`}
                                className={verticalClasses.pricingTableChargeRow}
                            >
                                <div
                                    className={
                                        item.discountAmount > 0 ? verticalClasses.hasDiscount : ''
                                    }
                                >
                                    <span>
                                        {item.chargeDescription}
                                        {item.chargePeriod && (
                                            <span className={verticalClasses.chargePeriod}>
                                                {item.chargePeriod}
                                            </span>
                                        )}
                                    </span>
                                    {item.discountAmount > 0 && (
                                        <div className={verticalClasses.discountBreakdown}>
                                            <div>
                                                <span>{t('wizard:pricingTable:initialAmount')}</span>
                                                <span>
                                                    {t('wizard:pricingTable:discount')}
                                                    {' '}
                                                    {item.discountName ? `(${item.discountName})` : ''}
                                                </span>
                                            </div>
                                            <span>{t('wizard:pricingTable:subtotal')}</span>
                                        </div>
                                    )}
                                </div>
                                <div
                                    className={`${verticalClasses.pricingTableChargeTotal} ${verticalClasses.fixedWidth}`}
                                >
                                    {item.discountAmount > 0 && (
                                        <div className={verticalClasses.discountBreakdown}>
                                            <div>
                                                <span>
                                                    {currencySymbol}
                                                    {numberRound(
                                                        item.initialChargeAmount
                              + (!showTaxRow ? item.initialTaxAmount : 0),
                                                    ).toFixed(2)}
                                                </span>
                                                <span>
                                                    -
                                                    {' '}
                                                    {currencySymbol}
                                                    {numberRound(
                                                        item.discountAmount
                              + (!showTaxRow ? 0 : item.discountTaxAmount),
                                                    ).toFixed(2)}
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                    <span>
                                        {currencySymbol}
                                        {!showTaxRow
                                            ? item.total.toFixed(2)
                                            : item.chargeAmount.toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        ))}
                    {showTaxRow && (
                        <div className={verticalClasses.pricingTableChargeRow}>
                            <div>
                                <span>{t('wizard:pricingTable:tax')}</span>
                            </div>
                            <div
                                className={
                                    `${verticalClasses.pricingTableChargeTotal
                                    } ${
                                        verticalClasses.fixedWidth}`
                                }
                            >
                                <span>
                                    {currencySymbol}
                                    {totalTax.toFixed(2)}
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className={verticalClasses.pricingTableBody}>
                    <div className={verticalClasses.pricingTableChargeRow}>
                        <div>
                            <ContentLoader
                                foregroundColor={rapidstorMainColor ?? location.skin.mainColour}
                                backgroundColor="#fff"
                                width="100%"
                                height="26"
                            >
                                <rect x="0" y="4" rx="3" ry="3" width="100%" height="26" />
                            </ContentLoader>
                        </div>
                        <div className={verticalClasses.fixedWidth}>
                            <ContentLoader
                                foregroundColor={rapidstorMainColor ?? location.skin.mainColour}
                                backgroundColor="#fff"
                                width="100%"
                                height="26"
                            >
                                <rect x="0" y="4" rx="3" ry="3" width="100%" height="26" />
                            </ContentLoader>
                        </div>
                    </div>
                </div>
            )}

            <div className={verticalClasses.pricingTableFooter}>
                {costRetrieve || charges ? (
                    <div>
                        <span>
                            {t('wizard:pricingTable:total')}
                            :
                            {currencySymbol}
                            {totalCost.toFixed(2)}
                        </span>
                        {!showTaxRow && (
                            <span className={verticalClasses.totalSubtext}>
                                (
                                {t('wizard:pricingTable:including')}
                                {' '}
                                {t('wizard:pricingTable:tax')}
                                )
                            </span>
                        )}
                    </div>
                ) : (
                    <div>
                        <ContentLoader
                            foregroundColor={rapidstorMainColor ?? location.skin.mainColour}
                            backgroundColor="#fff"
                            width="100%"
                            height="30"
                        >
                            <rect x="0" y="4" rx="3" ry="3" width="100%" height="30" />
                        </ContentLoader>
                    </div>
                )}
            </div>
        </div>
    );
}
